/**
 * Servicio de Mapa de Calor
 * 
 * Este servicio centraliza toda la lógica relacionada con el acceso
 * a la aplicación externa del mapa de calor, incluyendo:
 * - Construcción de URL con token de autenticación
 * - Validación de permisos
 * - Manejo de errores
 * - Generación de tokens temporales (preparado para implementación futura)
 * 
 * @module heatmap.service
 */

import { getToken, getUser } from '../shared/api/auth';
import { 
  HEATMAP_APP_URL, 
  HEATMAP_ENABLED, 
  HEATMAP_OPEN_MODE,
  buildHeatmapUrl as baseBuildheatmapUrl,
  isHeatmapConfigured 
} from '../config/heatmap.config';

/**
 * Nivel mínimo de AdminLevel requerido para acceder al mapa de calor
 * AdminLevel 3 = Super Admin
 */
const REQUIRED_ADMIN_LEVEL = 3;

/**
 * Obtiene el token de autenticación actual del usuario
 * @returns {string|null} Token actual o null si no existe
 */
export const getAuthToken = () => {
  return getToken();
};

/**
 * Verifica si el usuario tiene permisos para acceder al mapa de calor
 * @returns {boolean} true si el usuario tiene permiso
 */
export const userCanAccessHeatmap = () => {
  const user = getUser();
  
  if (!user) {
    return false;
  }

  // Verificar si el usuario es admin
  if (!user.isAdmin) {
    return false;
  }

  // Verificar nivel de admin
  const adminLevel = Number(user.AdminLevel) || 0;
  return adminLevel >= REQUIRED_ADMIN_LEVEL;
};

/**
 * Construye la URL del mapa de calor con el token actual
 * 
 * @param {Object} options - Opciones adicionales
 * @param {boolean} options.includeUserInfo - Si se debe incluir información del usuario (default: true)
 * @param {string} options.customToken - Token personalizado (si se quiere usar otro en lugar del actual)
 * @returns {string|null} URL construida o null si no es posible construirla
 * 
 * @example
 * // URL con token actual
 * const url = buildHeatmapUrlWithAuth();
 * // Resultado: https://mapa-calor.sibus.com.ar?token=abc123&source=portal&userId=43
 * 
 * @example
 * // URL sin información del usuario
 * const url = buildHeatmapUrlWithAuth({ includeUserInfo: false });
 * // Resultado: https://mapa-calor.sibus.com.ar?token=abc123
 */
export const buildHeatmapUrlWithAuth = (options = {}) => {
  const { 
    includeUserInfo = true, 
    customToken = null 
  } = options;

  // Obtener token (usar customToken si se provee, sino usar el actual)
  // 🔧 PUNTO DE EXTENSIÓN: En el futuro, aquí se puede reemplazar por un token temporal
  const token = customToken || getAuthToken();
  
  console.log('[HeatmapService] 🔑 Token obtenido:', token ? '✅ Existe (primeros 20 chars): ' + token.substring(0, 20) + '...' : '❌ NO EXISTE');
  
  if (!token) {
    console.warn('[HeatmapService] ⚠️ No se encontró token de autenticación');
    return null;
  }

  // Preparar parámetros
  const params = {
    token: token, // Token de autenticación
    source: 'portal', // Identificador de origen
    timestamp: Date.now() // Timestamp para prevenir cache
  };

  // Agregar información del usuario si está habilitado
  if (includeUserInfo) {
    const user = getUser();
    console.log('[HeatmapService] 👤 Usuario:', user);
    if (user) {
      params.userId = user.CdUsuario || user.Legajo || null;
      params.userName = user.Nombre || null;
      params.adminLevel = user.AdminLevel || null;
    }
  }

  console.log('[HeatmapService] 📋 Parámetros ANTES de llamar a buildHeatmapUrl:', params);
  console.log('[HeatmapService] 🔑 Verificación del token en params:', {
    existe: !!params.token,
    tipo: typeof params.token,
    longitud: params.token ? params.token.length : 0,
    primeros20: params.token ? params.token.substring(0, 20) : 'N/A'
  });

  // Construir URL usando la función base
  const finalUrl = baseBuildheatmapUrl(params);
  console.log('[HeatmapService] 🌐 URL generada completa:', finalUrl);
  console.log('[HeatmapService] 📦 Parámetros enviados:', params);
  
  return finalUrl;
};

/**
 * 🚀 PUNTO DE EXTENSIÓN FUTURA:
 * Esta función está preparada para generar tokens temporales específicos
 * para el mapa de calor en lugar de enviar el token principal del portal.
 * 
 * Implementación futura sugerida:
 * 1. Llamar a un endpoint del backend del portal: POST /api/auth/heatmap-token
 * 2. El backend genera un JWT temporal con expiración corta (ej: 5 minutos)
 * 3. Este token solo es válido para el mapa de calor
 * 4. Retornar este token para usarlo en buildHeatmapUrlWithAuth
 * 
 * @returns {Promise<string|null>} Token temporal o null si falla
 */
export const generateTemporaryHeatmapToken = async () => {
  // TODO: Implementar cuando el backend tenga el endpoint
  // 
  // try {
  //   const response = await fetch('/api/auth/heatmap-token', {
  //     method: 'POST',
  //     headers: {
  //       'Authorization': `Bearer ${getAuthToken()}`,
  //       'Content-Type': 'application/json'
  //     }
  //   });
  //   
  //   const data = await response.json();
  //   return data.temporaryToken;
  // } catch (error) {
  //   console.error('[HeatmapService] Error generando token temporal:', error);
  //   return null;
  // }
  
  console.warn('[HeatmapService] generateTemporaryHeatmapToken no está implementado aún');
  return null;
};

/**
 * Valida que todo esté configurado correctamente para acceder al mapa
 * @returns {Object} Resultado de la validación con información detallada
 * 
 * @example
 * const validation = validateHeatmapAccess();
 * if (!validation.canAccess) {
 *   console.error(validation.reason);
 * }
 */
export const validateHeatmapAccess = () => {
  console.log('[HeatmapService] 🔍 Iniciando validación de acceso...');
  
  // Verificar si está habilitado en config
  console.log('[HeatmapService] ⚙️ HEATMAP_ENABLED:', HEATMAP_ENABLED);
  if (!HEATMAP_ENABLED) {
    return {
      canAccess: false,
      reason: 'El acceso al mapa de calor está deshabilitado en la configuración.',
      code: 'DISABLED'
    };
  }

  // Verificar si está configurado
  const isConfigured = isHeatmapConfigured();
  console.log('[HeatmapService] ⚙️ isHeatmapConfigured():', isConfigured);
  console.log('[HeatmapService] ⚙️ HEATMAP_APP_URL:', HEATMAP_APP_URL);
  if (!isConfigured) {
    return {
      canAccess: false,
      reason: 'El mapa de calor no está configurado. Contacte al administrador.',
      code: 'NOT_CONFIGURED'
    };
  }

  // Verificar permisos del usuario
  const canAccess = userCanAccessHeatmap();
  const user = getUser();
  console.log('[HeatmapService] 👤 Usuario actual:', user);
  console.log('[HeatmapService] 🔐 userCanAccessHeatmap():', canAccess);
  if (!canAccess) {
    return {
      canAccess: false,
      reason: 'No tienes permisos para acceder al mapa de calor. Se requiere nivel de Super Admin.',
      code: 'NO_PERMISSION'
    };
  }

  // Verificar que exista token
  const token = getAuthToken();
  console.log('[HeatmapService] 🔑 Token existe:', token ? '✅ SÍ' : '❌ NO');
  if (!token) {
    return {
      canAccess: false,
      reason: 'No se encontró sesión activa. Por favor, inicia sesión nuevamente.',
      code: 'NO_TOKEN'
    };
  }

  console.log('[HeatmapService] ✅ Todas las validaciones pasaron correctamente');
  return {
    canAccess: true,
    reason: null,
    code: 'OK'
  };
};

/**
 * Función principal para abrir el mapa de calor
 * Realiza todas las validaciones y abre la aplicación externa
 * 
 * @param {Object} options - Opciones de apertura
 * @param {Function} options.onError - Callback para manejar errores
 * @param {Function} options.onSuccess - Callback cuando se abre correctamente
 * @param {boolean} options.useTemporaryToken - Si se debe usar token temporal (default: false)
 * @returns {Promise<boolean>} true si se abrió correctamente, false si hubo error
 * 
 * @example
 * // Uso básico
 * openHeatmapApp();
 * 
 * @example
 * // Uso con manejo de errores
 * openHeatmapApp({
 *   onError: (error) => toast.error(error.reason),
 *   onSuccess: () => toast.success('Abriendo mapa de calor...')
 * });
 */
export const openHeatmapApp = async (options = {}) => {
  const { 
    onError = null, 
    onSuccess = null,
    useTemporaryToken = false 
  } = options;

  // Validar acceso
  const validation = validateHeatmapAccess();
  
  if (!validation.canAccess) {
    console.error('[HeatmapService]', validation.reason);
    if (onError) {
      onError(validation);
    }
    return false;
  }

  try {
    let token = null;

    // 🔧 PUNTO DE EXTENSIÓN: Usar token temporal si está habilitado
    if (useTemporaryToken) {
      token = await generateTemporaryHeatmapToken();
      if (!token) {
        console.warn('[HeatmapService] No se pudo generar token temporal, usando token principal');
      }
    }

    // Construir URL con el token
    console.log('[HeatmapService] 🔨 Construyendo URL con autenticación...');
    const url = buildHeatmapUrlWithAuth({ 
      customToken: token,
      includeUserInfo: true 
    });

    if (!url) {
      console.error('[HeatmapService] ❌ No se pudo construir la URL');
      const errorInfo = {
        canAccess: false,
        reason: 'No se pudo construir la URL del mapa de calor',
        code: 'URL_BUILD_ERROR'
      };
      if (onError) {
        onError(errorInfo);
      }
      return false;
    }

    console.log('[HeatmapService] ✅ URL construida exitosamente');
    console.log('[HeatmapService] 🚀 Abriendo en modo:', HEATMAP_OPEN_MODE);
    console.log('[HeatmapService] 🔗 URL FINAL que se abrirá:', url);

    // Abrir en nueva pestaña (recomendado) o misma pestaña
    if (HEATMAP_OPEN_MODE === 'new-tab') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }

    // Callback de éxito
    if (onSuccess) {
      onSuccess();
    }

    console.log('[HeatmapService] ✅ Mapa de calor abierto correctamente');
    console.log('[HeatmapService] 💡 Revisa la URL en la nueva pestaña para ver el token');
    return true;

  } catch (error) {
    console.error('[HeatmapService] Error al abrir mapa de calor:', error);
    
    const errorInfo = {
      canAccess: false,
      reason: 'Error inesperado al abrir el mapa de calor',
      code: 'UNEXPECTED_ERROR',
      error
    };
    
    if (onError) {
      onError(errorInfo);
    }
    
    return false;
  }
};

/**
 * Hook helper para verificar si el botón/enlace debe mostrarse
 * @returns {boolean} true si se debe mostrar el acceso
 */
export const shouldShowHeatmapAccess = () => {
  return HEATMAP_ENABLED && 
         isHeatmapConfigured() && 
         userCanAccessHeatmap();
};

// Exportación por defecto con todas las funciones
const heatmapService = {
  // Funciones principales
  openHeatmapApp,
  buildHeatmapUrlWithAuth,
  validateHeatmapAccess,
  shouldShowHeatmapAccess,
  
  // Funciones auxiliares
  getAuthToken,
  userCanAccessHeatmap,
  
  // Funciones futuras
  generateTemporaryHeatmapToken,
  
  // Constantes
  REQUIRED_ADMIN_LEVEL,
  HEATMAP_APP_URL,
  HEATMAP_ENABLED
};

// 🧪 Función de debugging para probar en consola
window.testHeatmapUrl = () => {
  console.log('🧪 === TEST DE CONSTRUCCIÓN DE URL DEL MAPA DE CALOR ===');
  console.log('');
  
  console.log('1️⃣ Token en localStorage:');
  const token = localStorage.getItem('token');
  console.log('   ✅ Existe:', !!token);
  console.log('   📏 Longitud:', token ? token.length : 0);
  console.log('   🔤 Primeros 30 caracteres:', token ? token.substring(0, 30) + '...' : 'N/A');
  console.log('');
  
  console.log('2️⃣ Usuario en localStorage:');
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  console.log('   Usuario completo:', user);
  console.log('');
  
  console.log('3️⃣ Construyendo URL con token...');
  const url = buildHeatmapUrlWithAuth({ includeUserInfo: true });
  console.log('   URL final:', url);
  console.log('');
  
  console.log('4️⃣ Copiando URL al portapapeles...');
  if (url) {
    navigator.clipboard.writeText(url).then(() => {
      console.log('   ✅ URL copiada! Pégala en el navegador para probar.');
    }).catch(err => {
      console.log('   ⚠️ No se pudo copiar automáticamente. Copia manualmente la URL de arriba.');
    });
  }
  
  console.log('');
  console.log('🎯 Si la URL NO tiene el token en los query params, hay un problema.');
  console.log('🎯 La URL debería verse algo así:');
  console.log('   https://mapacalor.sibus.com.ar?token=ey...&source=portal&userId=43');
  
  return url;
};

console.log('🔧 Para probar la construcción de URL del mapa de calor, ejecuta en consola:');
console.log('   testHeatmapUrl()');

export default heatmapService;
